import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { RootState, useAppSelector } from "src/store";
import { MediaInfo } from "../types";
import { useAssetsApi } from "../api/AssetsApi";
import { resetError, resetList, setError, setFilter, setList, appendList, setLoading } from "../slices/assetLibrarySlice";

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    createdAt: "created_at",
    updatedAt: "updated_at",
  };

  const [field, order] = sort.split("_");
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

export const useAssetLibraryService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filter, list, error } = useAppSelector((state) => state.assetLibrary);

  const api = useAssetsApi();

  const getAssets = useCallback(async (companyId: string) => {
    const filter = getState().assetLibrary.filter;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getList: true }));

      const response = await api.getAssets({
        companyId,
        limit: filter.limit,
        offset: filter.offset,
        query: filter.query || undefined,
        sort: getServerSortField(filter.sort) || 'date_created_desc',
        mime_type: filter.mime_type || undefined,
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getList: false }));
    }
  }, []);

  const changeAssetLibraryFilter = useCallback((filter: Partial<RootState["assetLibrary"]["filter"]>) => {
    dispatch(setFilter(filter));
  }, []);

  const resetAssetLibrary = useCallback(() => {
    dispatch(resetList());
  }, []);

  const updateAssetList = useCallback((list: { items: MediaInfo[]; count: number }) => {
    dispatch(setList(list));
  }, [dispatch]);

  const loadMoreAssets = useCallback(async (companyId: string) => {
    const currentFilter = getState().assetLibrary.filter;
    const currentList = getState().assetLibrary.list;

    if (!currentList) return;

    const newOffset = currentList.items.length;

    if (newOffset >= currentList.count) {
      return;
    }

    try {
      dispatch(resetError());
      dispatch(setLoading({ loadMore: true }));

      const response = await api.getAssets({
        companyId,
        limit: currentFilter.limit,
        offset: newOffset,
        query: currentFilter.query || undefined,
        sort: getServerSortField(currentFilter.sort) || 'date_created_desc',
        mime_type: currentFilter.mime_type || undefined,
      });

      dispatch(appendList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ loadMore: false }));
    }
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getAssets,
    loadMoreAssets,
    changeAssetLibraryFilter,
    resetAssetLibrary,
    updateAssetList,
  };
}; 


